import { useEffect, useRef } from 'react';

/**
 * Re-runs `onResume` whenever the tab returns to the foreground or the network
 * reconnects. Mobile browsers (especially iOS Safari) suspend the page and
 * abort in-flight requests when backgrounded; without this, data screens get
 * stuck on an error state after the user switches apps and comes back.
 *
 * Uses a ref so it subscribes only once and works even when `onResume` is a
 * non-memoized function (no infinite re-subscribe / refetch loop).
 */
export function useResumeRefresh(onResume) {
  const cb = useRef(onResume);
  cb.current = onResume;

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        try {
          cb.current && cb.current();
        } catch (e) {
          /* swallow — recovery is best-effort */
        }
      }
    };
    document.addEventListener('visibilitychange', handler);
    window.addEventListener('pageshow', handler);
    window.addEventListener('online', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
      window.removeEventListener('pageshow', handler);
      window.removeEventListener('online', handler);
    };
  }, []);
}

export default useResumeRefresh;
